/** Inspired by, but modified, https://gist.github.com/revolunet/e620e2c532b7144c62768a36b8b96da2 */

/**
 * Plays a complete audio buffer (ArrayBuffer) in one shot.
 * Used as a fallback when ReadableStream is not available (e.g. iOS/mobile Tauri).
 * Accepts an existing AudioContext to support iOS which requires it to be created
 * during a user gesture.
 */
export function playAudioBuffer(
  eventId: string | number,
  arrayBuffer: ArrayBuffer,
  onDoneOrCancel?: (eventId: string | number) => void,
  existingContext?: AudioContext
) {
  const context = existingContext || new AudioContext();
  let source: AudioBufferSourceNode | null = null;
  let cancelled = false;

  context.decodeAudioData(
    arrayBuffer,
    (buffer) => {
      if (cancelled) return;
      source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(context.destination);
      source.addEventListener('ended', () => {
        if (!cancelled) onDoneOrCancel?.(eventId);
      });
      source.start(0);
    },
    (err) => {
      console.error('Failed to decode TTS audio buffer:', err);
      onDoneOrCancel?.(eventId);
    }
  );

  return {
    eventId,
    cancel: () => {
      cancelled = true;
      try {
        source?.stop();
      } catch {
        // source may not have started yet
      }
      onDoneOrCancel?.(eventId);
    },
  };
}

/**
 * Plays an audio stream, which is a ReadableStream of AudioBuffers.
 */
export function playAudioStream(
  /**
   * Make this a universally generated ID, which will get passed in onDone,
   * allowing you to know which audio play request was finished.
   * You want this because of race conditions that can occur when users play or cancel audio in quick succession.
   * */
  eventId: string | number,
  stream: ReadableStream,
  onDoneOrCancel?: (eventId: string | number) => void,
  existingContext?: AudioContext
) {
  let lastSource: AudioBufferSourceNode;
  const context = existingContext || new AudioContext();
  const audioStack = [];
  const allAudioSources: AudioBufferSourceNode[] = [];
  let nextTime = 0;
  const reader = stream.getReader();
  let cancelled = false;
  async function read() {
    if (cancelled) return;
    const { value, done } = await reader.read();
    if (value)
      await new Promise<void>((res, rej) =>
        context.decodeAudioData(
          value.buffer,
          function (buffer) {
            audioStack.push(buffer);
            if (audioStack.length) {
              scheduleBuffers();
              res();
            }
          },
          function (err) {
            rej(err);
          }
        )
      );
    if (done) {
      if (lastSource)
        lastSource.addEventListener('ended', () => {
          onDoneOrCancel?.(eventId);
        });
      return;
    }

    read();
  }
  read();

  function scheduleBuffers() {
    while (audioStack.length && !cancelled) {
      const buffer = audioStack.shift();
      const source = context.createBufferSource();
      allAudioSources.push(source);
      source.buffer = buffer;
      source.connect(context.destination);
      if (nextTime == 0) nextTime = context.currentTime + 0.01;
      source.start(nextTime);
      nextTime += source.buffer.duration - 0.001;
      lastSource = source;
    }
  }
  return {
    eventId,
    cancel: () => {
      cancelled = true;
      allAudioSources.forEach((source) => {
        source.stop();
      });

      onDoneOrCancel(eventId);
    },
  };
}
